import {
  DEFAULT_LIMITS,
  DepositError,
  imageHashes,
  prepareDeposit,
  saveDeposit,
  withOrder,
  type DepositBackend,
  type DepositSummary,
  type DuplicateCheck,
  type PreparedDeposit,
  type SaveProgress,
} from "@opulancia/deposit-core";
import type { App } from "../app.js";
import { announce, formatBytes, h, notice, plural, replace } from "../dom.js";
import { orderEditor, type OrderEditor } from "./reorder.js";

type Phase =
  | { kind: "pick"; error?: { message: string; issues: string[] } }
  | { kind: "reading" }
  | { kind: "review" }
  | { kind: "saving"; progress: SaveProgress }
  | { kind: "interrupted"; reason: string; deposit: DepositSummary | null }
  | { kind: "duplicate"; deposit: DepositSummary; unfinished: boolean }
  | { kind: "conflict"; deposit: DepositSummary }
  | { kind: "saved"; deposit: DepositSummary; uploaded: number; resumed: boolean };

interface Draft {
  deposit: PreparedDeposit;
  thumbs: Map<string, string>;
  editor: OrderEditor;
  duplicates: DuplicateCheck | null;
  note: string;
  /** The exact selection and order sent, kept for « Reprendre l'envoi ». */
  attempt: { clientKey: string; ordered: PreparedDeposit } | null;
}

/** The « Ajouter » screen: choose a ZIP or images → check the order → Enregistrer. */
export class AddScreen {
  private phase: Phase = { kind: "pick" };
  private draft: Draft | null = null;
  private root: HTMLElement = h("div");

  constructor(private readonly app: App) {}

  render(): HTMLElement {
    this.root = h("div", { class: "add" });
    this.paint();
    return this.root;
  }

  private set(phase: Phase): void {
    this.phase = phase;
    this.app.setBusy(phase.kind === "saving" || phase.kind === "interrupted");
    this.paint();
  }

  private paint(): void {
    if (!this.root.isConnected && this.root.childElementCount) return;
    const p = this.phase;
    const step = p.kind === "pick" || p.kind === "reading" ? 1 : p.kind === "review" ? 2 : 3;
    replace(
      this.root,
      h("h1", null, "Ajouter un carrousel"),
      h("p", { class: "lede" }, "Déposez le ZIP ou les images finales, vérifiez l'ordre, enregistrez. Rien d'autre n'est demandé."),
      h(
        "ol",
        { class: "steps", "aria-label": "Étapes" },
        ["Choisir", "Vérifier l'ordre", "Enregistrer"].map((label, i) =>
          h("li", { class: i + 1 < step ? "done" : "", "aria-current": i + 1 === step ? "step" : false }, h("b", null, i + 1 < step ? "✓" : String(i + 1)), label),
        ),
      ),
      p.kind === "pick" ? this.pick(p.error) : p.kind === "reading" ? notice("info", "Lecture des fichiers…", "Les images sont extraites telles quelles, sans compression ni conversion.") : this.review(),
    );
  }

  // ---------- 1. Choose ----------
  private pick(error?: { message: string; issues: string[] }): HTMLElement {
    const input = h("input", {
      type: "file",
      multiple: true,
      class: "sr-only",
      id: "files",
      accept: ".zip,application/zip,image/png,image/jpeg,image/webp,.txt,.md,.json,.csv,.pdf",
      onchange: () => input.files && this.read([...input.files]),
    });
    const zone = h(
      "div",
      { class: "dropzone", "data-testid": "dropzone" },
      h("div", { class: "glyph", "aria-hidden": "true" }, "+"),
      h("strong", null, "Déposez un ZIP ou des images"),
      h("p", null, "ou"),
      h("label", { class: "btn primary big", for: "files" }, "Choisir des fichiers"),
      input,
      h("p", { class: "formats" }, "ZIP, PNG, JPEG ou WebP — jusqu'à 20 slides. Les fichiers .txt, .md, .json, .csv et .pdf joints sont conservés."),
    );
    zone.addEventListener("dragover", (e) => {
      e.preventDefault();
      zone.classList.add("over");
    });
    zone.addEventListener("dragleave", () => zone.classList.remove("over"));
    zone.addEventListener("drop", (e) => {
      e.preventDefault();
      zone.classList.remove("over");
      if (e.dataTransfer?.files.length) void this.read([...e.dataTransfer.files]);
    });
    return h(
      "div",
      null,
      error ? h("div", { style: "margin-bottom:16px" }, notice("bad", error.message, error.issues.length ? h("ul", null, error.issues.map((i) => h("li", null, i))) : "Choisissez d'autres fichiers.")) : null,
      zone,
    );
  }

  private async read(files: File[]): Promise<void> {
    this.set({ kind: "reading" });
    try {
      const inputs = await Promise.all(files.map(async (f) => ({ name: f.name, data: new Uint8Array(await f.arrayBuffer()) })));
      const deposit = await prepareDeposit(inputs, DEFAULT_LIMITS);
      this.startReview(deposit);
    } catch (error) {
      const e = error instanceof DepositError ? error : new DepositError("Ces fichiers ne peuvent pas être lus.", [String(error)]);
      this.set({ kind: "pick", error: { message: e.message, issues: e.issues } });
    }
  }

  private startReview(deposit: PreparedDeposit): void {
    this.dispose();
    const thumbs = new Map([...deposit.slides, ...deposit.extras].map((f) => [f.key, URL.createObjectURL(new Blob([f.data as Uint8Array<ArrayBuffer>], { type: f.mediaType }))]));
    const item = (f: PreparedDeposit["slides"][number]) => ({ key: f.key, name: f.sourcePath.split("/").pop() ?? f.sourcePath, url: thumbs.get(f.key)! });
    const editor = orderEditor({ slides: deposit.slides.map(item), extras: deposit.extras.map(item), maxSlides: DEFAULT_LIMITS.maxSlides });
    this.draft = { deposit, thumbs, editor, duplicates: null, note: "", attempt: null };
    this.set({ kind: "review" });
    announce(`${plural(deposit.slides.length, "slide prête", "slides prêtes")}. Vérifiez l'ordre puis enregistrez.`);
    const draft = this.draft;
    void this.app.connection.backend.findDuplicates(deposit.zip?.sha256 ?? null, imageHashes(deposit)).then(
      (check) => {
        if (this.draft !== draft) return;
        draft.duplicates = check;
        if (this.phase.kind === "review") this.paint();
      },
      () => {},
    );
  }

  private dispose(): void {
    for (const url of this.draft?.thumbs.values() ?? []) URL.revokeObjectURL(url);
    this.draft = null;
  }

  private reset(): void {
    this.dispose();
    this.set({ kind: "pick" });
  }

  // ---------- 2. Review, 3. Save ----------
  private review(): HTMLElement {
    const d = this.draft!;
    const p = this.phase;
    const editing = p.kind === "review";
    return h(
      "div",
      { class: "layout" },
      h("section", { "aria-label": "Ordre du carrousel" }, editing ? d.editor.element : this.frozenOrder(d)),
      h("aside", { class: "side" }, this.status(), this.contents(d)),
    );
  }

  /** Once sent, the order shown is the one being saved (it can be corrected later from the deposit). */
  private frozenOrder(d: Draft): HTMLElement {
    const ordered = d.attempt?.ordered ?? d.deposit;
    return h(
      "ol",
      { class: "slides compact", "aria-label": "Ordre envoyé" },
      ordered.slides.map((f, i) =>
        h(
          "li",
          { class: "slide" },
          h("div", { class: "cover" }, h("img", { src: d.thumbs.get(f.key)!, alt: `Slide ${i + 1}` })),
          h("span", { class: "pos" }, String(i + 1)),
        ),
      ),
    );
  }

  private status(): HTMLElement {
    const p = this.phase;
    const d = this.draft!;
    switch (p.kind) {
      case "review": {
        const dup = d.duplicates;
        const exact = dup?.exact;
        const n = d.editor.slides().length;
        return h(
          "div",
          { class: "panel" },
          exact && exact.upload_status === "complete"
            ? notice("warn", "Déjà dans la bibliothèque", `Ce carrousel correspond au dépôt n°${exact.number}. Il ne sera pas enregistré deux fois.`, h("a", { class: "btn", href: `#/depot/${exact.id}` }, `Voir le dépôt n°${exact.number}`))
            : exact
              ? notice("info", "Envoi inachevé retrouvé", `Ce carrousel a déjà commencé à être envoyé (dépôt n°${exact.number}). Enregistrer reprendra l'envoi.`)
              : null,
          dup?.overlaps.length
            ? h("p", { class: "muted", style: "font-size:14px" }, `Images en commun avec ${dup.overlaps.map((o) => `le dépôt n°${o.number} (${o.shared_images})`).join(", ")}. Le dépôt reste possible.`)
            : null,
          h(
            "details",
            { class: "note" },
            h("summary", null, "Ajouter une note (facultatif)"),
            h("textarea", { maxlength: DEFAULT_LIMITS.maxNoteChars, "aria-label": "Note facultative", oninput: (e: Event) => (d.note = (e.target as HTMLTextAreaElement).value) }, d.note),
          ),
          h(
            "div",
            { class: "sticky-save" },
            exact && exact.upload_status === "complete"
              ? h("button", { class: "btn big", type: "button", onclick: () => this.reset() }, "Choisir d'autres fichiers")
              : h("button", { class: "btn primary big", type: "button", "data-testid": "save", onclick: () => void this.save() }, `Enregistrer ${plural(n, "slide", "slides")}`),
          ),
          h("p", { class: "muted", style: "font-size:13px;margin:10px 0 0" }, "« Enregistré » s'affiche seulement quand le serveur a confirmé chaque fichier."),
          h("button", { class: "btn quiet", type: "button", onclick: () => this.reset() }, "Changer de fichiers"),
        );
      }
      case "saving": {
        const pr = p.progress;
        const done = pr.phase === "uploading" ? pr.done : pr.phase === "completing" ? 1 : 0;
        const total = pr.phase === "uploading" ? Math.max(pr.total, 1) : 1;
        const label = pr.phase === "starting" ? "Préparation de l'envoi…" : pr.phase === "uploading" ? `Envoi des fichiers : ${pr.done} sur ${pr.total}` : "Vérification par le serveur…";
        return h(
          "div",
          { class: "panel", "aria-busy": "true" },
          h("h2", null, "Enregistrement en cours"),
          h("div", { class: "progress-label" }, h("span", null, label), h("span", null, `${Math.round((done / total) * 100)} %`)),
          h("div", { class: "progress", role: "progressbar", "aria-valuemin": "0", "aria-valuemax": String(total), "aria-valuenow": String(done), "aria-label": label }, h("span", { style: `width:${(done / total) * 100}%` })),
          h("p", { class: "muted", style: "font-size:13px;margin:12px 0 0" }, "Gardez cette page ouverte. En cas de coupure, l'envoi reprendra où il s'est arrêté."),
        );
      }
      case "interrupted":
        return notice(
          "warn",
          "Envoi interrompu",
          h(
            "div",
            null,
            h("p", null, `${p.reason}. Les fichiers déjà envoyés sont conservés ; le dépôt n'est pas marqué « enregistré » tant que tout n'est pas confirmé.`),
            h("p", { class: "muted", style: "font-size:13px;margin-top:6px" }, "Page fermée entre-temps ? Redéposez les mêmes fichiers : l'envoi reprendra."),
          ),
          h("button", { class: "btn primary", type: "button", "data-testid": "resume", onclick: () => void this.save(true) }, "Reprendre l'envoi"),
        );
      case "duplicate":
        return p.unfinished
          ? notice("warn", "Ce carrousel est en cours d'envoi ailleurs", `Le dépôt n°${p.deposit.number} a commencé avec d'autres fichiers d'origine (par exemple le ZIP). Redéposez ces fichiers-là pour le terminer.`, h("a", { class: "btn", href: `#/depot/${p.deposit.id}` }, `Voir le dépôt n°${p.deposit.number}`), h("button", { class: "btn quiet", type: "button", onclick: () => this.reset() }, "Ajouter un autre carrousel"))
          : notice("warn", "Déjà dans la bibliothèque", `Ce carrousel est le dépôt n°${p.deposit.number}. Rien n'a été enregistré en double.`, h("a", { class: "btn", href: `#/depot/${p.deposit.id}` }, `Voir le dépôt n°${p.deposit.number}`), h("button", { class: "btn quiet", type: "button", onclick: () => this.reset() }, "Ajouter un autre carrousel"));
      case "conflict":
        return notice("bad", "Un fichier stocké ne correspond pas", `Un original existe déjà sous le même nom avec un autre contenu. Rien n'a été remplacé. Signalez le dépôt n°${p.deposit.number}.`, h("button", { class: "btn", type: "button", onclick: () => this.reset() }, "Revenir"));
      case "saved":
        return notice(
          "ok",
          "Enregistré",
          h(
            "div",
            null,
            h("p", { "data-testid": "saved" }, `Dépôt n°${p.deposit.number} — ${plural(p.deposit.slide_count, "slide", "slides")}. Le serveur a confirmé la présence de chaque fichier.`),
            h("p", { class: "muted", style: "font-size:13px;margin-top:6px" }, "Intégrité : contrôle du serveur à venir, avant toute analyse. Le lien Instagram s'ajoute depuis le dépôt, quand vous voulez."),
          ),
          h("a", { class: "btn dark", href: `#/depot/${p.deposit.id}` }, "Voir le dépôt"),
          h("button", { class: "btn", type: "button", onclick: () => this.reset() }, "Ajouter un autre carrousel"),
        );
      default:
        return h("div");
    }
  }

  private contents(d: Draft): HTMLElement {
    const dep = d.deposit;
    const kept = [
      dep.zip ? `ZIP d'origine : ${dep.zip.sourcePath} (${formatBytes(dep.zip.bytes)})` : null,
      `${plural(dep.slides.length + dep.extras.length, "image conservée telle quelle", "images conservées telles quelles")}`,
      ...dep.sidecars.map((s) => `${s.sourcePath.split("/").pop()} (fichier descriptif)`),
    ].filter(Boolean) as string[];
    return h(
      "div",
      { class: "panel" },
      h("h2", null, "Conservé"),
      h("ul", null, kept.map((k) => h("li", null, k))),
      dep.warnings.length ? h("div", { style: "margin-top:12px" }, h("h2", null, "À noter"), h("ul", null, dep.warnings.map((w) => h("li", null, w)))) : null,
      dep.skipped.length
        ? h(
            "div",
            { style: "margin-top:12px" },
            h("h2", null, "Non repris"),
            h("ul", null, dep.skipped.map((s) => h("li", null, `${s.path.split("/").pop()} : ${s.reason}${dep.zip ? " (reste dans le ZIP)" : ""}`))),
          )
        : null,
    );
  }

  private async save(resume = false): Promise<void> {
    const d = this.draft;
    if (!d) return;
    if (resume) this.app.connection.restoreNetwork();
    if (!d.attempt || !resume) {
      try {
        d.attempt = { clientKey: crypto.randomUUID(), ordered: withOrder(d.deposit, d.editor.slides(), DEFAULT_LIMITS) };
      } catch (error) {
        announce(error instanceof Error ? error.message : String(error));
        return;
      }
    }
    const { clientKey, ordered } = d.attempt;
    const backend = this.app.connection.backend;
    this.set({ kind: "saving", progress: { phase: "starting" } });
    const result = await saveDeposit(backend, ordered, {
      clientKey,
      note: d.note,
      limits: DEFAULT_LIMITS,
      onProgress: (progress) => {
        if (this.draft !== d) return;
        this.phase = { kind: "saving", progress };
        this.paint();
      },
    });
    if (this.draft !== d) return;
    switch (result.status) {
      case "saved": {
        // resuming a deposit started with another order: the order chosen here wins
        if (result.resumed) await alignOrder(backend, result.deposit.id, ordered).catch(() => {});
        this.set({ kind: "saved", deposit: result.deposit, uploaded: result.uploaded, resumed: result.resumed });
        announce(`Enregistré : dépôt numéro ${result.deposit.number}.`);
        break;
      }
      case "duplicate":
        this.set({ kind: "duplicate", deposit: result.deposit, unfinished: result.unfinished });
        announce(`Déjà dans la bibliothèque : dépôt numéro ${result.deposit.number}.`);
        break;
      case "conflict":
        this.set({ kind: "conflict", deposit: result.deposit });
        break;
      case "interrupted":
        this.set({ kind: "interrupted", reason: result.reason.replace(/\.$/, ""), deposit: result.deposit });
        announce("Envoi interrompu. Reprenez l'envoi quand la connexion revient.");
        break;
    }
  }
}

/** Applies the chosen order to a deposit resumed from an earlier attempt, if it differs. */
async function alignOrder(backend: DepositBackend, depositId: string, ordered: PreparedDeposit): Promise<void> {
  const detail = await backend.getDeposit(depositId);
  const images = detail.files.filter((f) => f.role === "slide" || f.role === "extra_image");
  const free = [...images];
  const slideIds: string[] = [];
  for (const s of ordered.slides) {
    const i = free.findIndex((f) => f.sha256 === s.sha256 && f.source_path === s.sourcePath.slice(0, 500));
    const j = i >= 0 ? i : free.findIndex((f) => f.sha256 === s.sha256);
    if (j < 0) return;
    slideIds.push(free.splice(j, 1)[0]!.id);
  }
  const current = images.filter((f) => f.role === "slide").map((f) => f.id);
  if (current.join() === slideIds.join()) return;
  await backend.setSlideOrder(depositId, detail.deposit.version, slideIds, free.map((f) => f.id));
}
